import React, { useMemo } from "react";
import { Activity, Clock, AlertOctagon, Flag, Inbox, CheckCircle2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TEAM_META, STATUS_META, fmtDuration } from "./shared";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--destructive) / 0.7)",
  medium:   "hsl(var(--warning))",
  low:      "hsl(var(--primary))",
};

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

const KpiTile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="panel p-3 flex flex-col gap-0.5 min-w-0" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="micro-label">{label}</span>
      <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
    </div>
    <div className={`font-mono text-2xl font-semibold tracking-tighter leading-none mt-1 ${accent || ""}`}>
      {value}
    </div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">
      {sub}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="panel-elevated p-2 text-xs font-mono">
      {label && <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.dataKey || p.name}</span>
          <span className="ml-auto">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export const IncidentAnalytics = ({ analytics }) => {
  const byType = analytics?.by_type ?? [];
  const bySev = analytics?.by_severity ?? [];
  const byStatus = analytics?.by_status ?? [];
  const byTeam = analytics?.by_team ?? [];
  const hourly = analytics?.hourly ?? [];

  const sevData = useMemo(
    () => bySev.map((s) => ({ name: s.severity, value: s.count, fill: SEV_COLOR[s.severity] })),
    [bySev]
  );

  const typeData = useMemo(
    () => byType.slice(0, 8).map((t) => ({ name: t.type.replace(/_/g, " "), count: t.count })),
    [byType]
  );

  return (
    <div className="space-y-2">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiTile icon={Inbox}        label="Total Incidents" value={fmt(analytics?.total)}   sub="this session" testid="ia-total" />
        <KpiTile icon={Activity}     label="Open Now"        value={fmt(analytics?.open)}    sub="ack / dispatched" accent={analytics?.open > 0 ? "sev-medium" : ""} testid="ia-open" />
        <KpiTile icon={CheckCircle2} label="Resolved"        value={fmt(analytics?.resolved)} sub={`${analytics?.reviewed ?? 0} reviewed`} testid="ia-resolved" />
        <KpiTile icon={Clock}        label="MTTR"            value={fmtDuration(analytics?.mttr_seconds)} sub="mean time to resolve" testid="ia-mttr" />
        <KpiTile
          icon={Flag}
          label="False-Positive Rate"
          value={`${Math.round((analytics?.false_positive_rate ?? 0) * 100)}%`}
          sub={`${analytics?.false_positive ?? 0} flagged`}
          accent={(analytics?.false_positive_rate ?? 0) > 0.15 ? "sev-medium" : ""}
          testid="ia-fp"
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <div className="panel min-h-[280px] flex flex-col">
          <div className="px-3 py-1.5 border-b border-border h-widget">Incidents by Type</div>
          <div className="flex-1 p-2 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill="hsl(var(--primary))" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel min-h-[280px] flex flex-col">
          <div className="px-3 py-1.5 border-b border-border h-widget">By Severity</div>
          <div className="flex-1 p-2 min-h-0 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sevData} dataKey="value" nameKey="name" innerRadius="42%" outerRadius="72%" isAnimationActive={false} stroke="hsl(var(--surface))">
                  {sevData.map((s, i) => <Cell key={i} fill={s.fill} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", textTransform: "uppercase", letterSpacing: 1 }}
                  iconType="square"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <div className="panel min-h-[220px] flex flex-col lg:col-span-2">
          <div className="px-3 py-1.5 border-b border-border h-widget">Incidents · last 6 hours</div>
          <div className="flex-1 p-2 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" allowDecimals={false} width={32} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill="hsl(var(--primary))" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel min-h-[220px] flex flex-col">
          <div className="px-3 py-1.5 border-b border-border h-widget">By Responder Team</div>
          <div className="flex-1 p-2 min-h-0 flex flex-col gap-1.5 justify-center">
            {byTeam.map((t) => {
              const meta = TEAM_META[t.team];
              const Icon = meta?.icon;
              const total = analytics?.total || 1;
              const pct = Math.round((t.count / total) * 100);
              return (
                <div key={t.team} className="flex items-center gap-2" data-testid={`ia-team-${t.team}`}>
                  {Icon && <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: meta.color }} />}
                  <span className="font-display font-bold text-xs uppercase tracking-tight w-24 shrink-0" style={{ color: meta?.color }}>{meta?.label || t.team}</span>
                  <div className="flex-1 h-1.5 bg-border">
                    <div className="h-full" style={{ width: `${pct}%`, background: meta?.color || "hsl(var(--primary))" }} />
                  </div>
                  <span className="font-mono text-xs w-8 text-right">{t.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="panel p-3 flex flex-wrap items-center gap-2">
        <span className="h-widget mr-3">Status distribution</span>
        {byStatus.map((s) => {
          const meta = STATUS_META[s.status];
          return (
            <div key={s.status} className="flex items-center gap-1.5" data-testid={`ia-status-${s.status}`}>
              <span className="w-2 h-2" style={{ background: meta?.color }} />
              <span className="font-display font-bold text-[11px] uppercase tracking-tight" style={{ color: meta?.color }}>{meta?.label}</span>
              <span className="font-mono text-xs">{s.count}</span>
              <AlertOctagon className="w-0 h-0" /> {/* avoid unused import warning */}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IncidentAnalytics;
